import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuthStore } from '../../../../store/authStore';
import { MAX_CONTENT_HEIGHT, MAX_CONTENT_WIDTH } from '../constants';
import { scaleWithAspect } from '../aspectScale';
import { safeNodeAt } from '../../../lib/safeNodeAt';
import { downloadAsset } from '../../../lib/downloadAsset';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;

const useImageProperties = ({ selectedNode, selectedNodePos, editor, onClose }) => {
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [rotation, setRotation] = useState(0);
  const [wrap, setWrap] = useState('inline');
  const [alt, setAlt] = useState('');
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [naturalWidth, setNaturalWidth] = useState(null);
  const [naturalHeight, setNaturalHeight] = useState(null);
  // T-042.img: unlocked by DEFAULT so the height field always takes effect. With
  // the lock ON, a full-width image pins the width at MAX and any typed height is
  // re-derived back down (looked "frozen"). The user can re-enable the lock.
  const [aspectRatioLocked, setAspectRatioLocked] = useState(false);
  const [borderColor, setBorderColor] = useState('#000000');
  const [borderWidth, setBorderWidth] = useState(0);
  const [borderStyle, setBorderStyle] = useState('solid');
  const [borderRadius, setBorderRadius] = useState(0);
  const [opacity, setOpacity] = useState(100);

  const holdIntervalRef = useRef(null);
  const holdTimeoutRef = useRef(null);

  const { token } = useAuthStore();

  const selectedNodeIsImage =
    selectedNode &&
    (selectedNode.type.name === 'image' || selectedNode.type.name === 'resizableImage');

  useEffect(() => {
    if (!selectedNodeIsImage) return;

    // The rendered <img> carries a *signed* src (short-lived URL), not the
    // canonical /assets path stored in the node, so querying by src never
    // matches → naturalWidth/Height stay null → aspect-ratio lock is dead.
    // Resolve the real <img> from the node's DOM instead (robust to signing).
    let img = null;
    if (editor && selectedNodePos !== null && selectedNodePos !== undefined) {
      const dom = editor.view.nodeDOM(selectedNodePos);
      if (dom) {
        img = dom.tagName === 'IMG' ? dom : dom.querySelector?.('img');
      }
    }
    if (!img) {
      // Fallback for legacy/edge cases (e.g. external URL src === signed src).
      img = document.querySelector(`img[src="${selectedNode.attrs.src}"]`);
    }
    if (img) {
      if (img.complete && img.naturalWidth) {
        setNaturalWidth(img.naturalWidth);
        setNaturalHeight(img.naturalHeight);
      } else {
        img.onload = () => {
          setNaturalWidth(img.naturalWidth);
          setNaturalHeight(img.naturalHeight);
        };
      }
    }

    setWidth(selectedNode.attrs.width || '');
    setHeight(selectedNode.attrs.height || '');
    setRotation(selectedNode.attrs.rotation || 0);
    setWrap(selectedNode.attrs.wrap || 'inline');
    setAlt(selectedNode.attrs.alt || '');
    setBorderColor(selectedNode.attrs.borderColor || '#000000');
    setBorderWidth(selectedNode.attrs.borderWidth || 0);
    setBorderStyle(selectedNode.attrs.borderStyle || 'solid');
    setBorderRadius(selectedNode.attrs.borderRadius || 0);
    setOpacity(selectedNode.attrs.opacity !== undefined ? selectedNode.attrs.opacity : 100);
  }, [selectedNodeIsImage, selectedNode, selectedNodePos, editor]);

  const updateImageAttribute = useCallback(
    (attrs, shouldFocus = false) => {
      if (!editor || !selectedNodeIsImage) return false;

      // T-035.1 guard: act only when the remembered position still points at an
      // image node. A no-op on an invalid pos must NOT let the caller commit local
      // panel state (avoids the "panel remembers a size it never applied" desync).
      const pos = selectedNodePos;
      const nodeAtPos = safeNodeAt(editor.state.doc, pos);
      const posIsImage =
        nodeAtPos &&
        (nodeAtPos.type.name === 'image' || nodeAtPos.type.name === 'resizableImage');
      if (!posIsImage) return false;

      const currentAttrs = {
        ...selectedNode.attrs,
        width: width ? parseInt(width) : selectedNode.attrs.width,
        height: height ? parseInt(height) : selectedNode.attrs.height,
        rotation: rotation !== undefined ? rotation : selectedNode.attrs.rotation,
        wrap: wrap || selectedNode.attrs.wrap,
        alt: alt || selectedNode.attrs.alt,
        borderColor,
        borderWidth,
        borderStyle,
        borderRadius,
        opacity,
        ...attrs,
      };

      const nodeType = selectedNode.type.name;
      const chain = editor.chain();

      // Re-affirm the NodeSelection on every update so the panel stays bound to
      // the image even after focus briefly moved to a panel control (T-035.1).
      chain.setNodeSelection(pos);

      if (shouldFocus) {
        // scrollIntoView:false prevents a scroll-jump when re-focusing the editor
        // from a panel control.
        chain.focus(undefined, { scrollIntoView: false });
      }

      chain.updateAttributes(nodeType, currentAttrs).run();
      return true;
    },
    [
      editor,
      selectedNodeIsImage,
      selectedNode,
      selectedNodePos,
      width,
      height,
      rotation,
      wrap,
      alt,
      borderColor,
      borderWidth,
      borderStyle,
      borderRadius,
      opacity,
    ],
  );

  const handleWidthChange = (e) => {
    const val = e.target.value;
    const numVal = parseInt(val);

    // Aspect-locked: scale by the limiting axis so BOTH dims fit while the ratio
    // is preserved (T-035.2). Never discards the driven value.
    if (aspectRatioLocked && naturalWidth && naturalHeight && val && !Number.isNaN(numVal)) {
      const { width: newWidth, height: newHeight } = scaleWithAspect({
        driver: 'width',
        value: numVal,
        naturalWidth,
        naturalHeight,
        maxWidth: MAX_CONTENT_WIDTH,
        maxHeight: MAX_CONTENT_HEIGHT,
      });
      if (updateImageAttribute({ width: newWidth, height: newHeight })) {
        setWidth(newWidth);
        setHeight(newHeight);
      }
      return;
    }

    // Aspect unlocked (or naturals unknown): clamp the driven axis only.
    if (numVal > MAX_CONTENT_WIDTH) {
      toast.error(`El ancho máximo es ${MAX_CONTENT_WIDTH}px (área de márgenes)`);
      if (updateImageAttribute({ width: MAX_CONTENT_WIDTH })) setWidth(MAX_CONTENT_WIDTH);
      return;
    }

    if (!val) {
      setWidth(val);
      return;
    }
    if (updateImageAttribute({ width: numVal })) setWidth(val);
  };

  const handleHeightChange = (e) => {
    const val = e.target.value;
    const numVal = parseInt(val);

    // Aspect-locked: scale by the limiting axis (T-035.2).
    if (aspectRatioLocked && naturalWidth && naturalHeight && val && !Number.isNaN(numVal)) {
      const { width: newWidth, height: newHeight } = scaleWithAspect({
        driver: 'height',
        value: numVal,
        naturalWidth,
        naturalHeight,
        maxWidth: MAX_CONTENT_WIDTH,
        maxHeight: MAX_CONTENT_HEIGHT,
      });
      if (updateImageAttribute({ width: newWidth, height: newHeight })) {
        setHeight(newHeight);
        setWidth(newWidth);
      }
      return;
    }

    // Aspect unlocked (or naturals unknown): clamp the driven axis only.
    if (numVal > MAX_CONTENT_HEIGHT) {
      toast.error(`El alto máximo es ${MAX_CONTENT_HEIGHT}px (área de márgenes)`);
      if (updateImageAttribute({ height: MAX_CONTENT_HEIGHT })) setHeight(MAX_CONTENT_HEIGHT);
      return;
    }

    if (!val) {
      setHeight(val);
      return;
    }
    if (updateImageAttribute({ height: numVal })) setHeight(val);
  };

  const handleRotationChange = (e) => {
    const val = parseInt(e.target.value) || 0;
    setRotation(val);
    updateImageAttribute({ rotation: val });
  };

  const handleRotateButton = (degrees) => {
    const newRotation = (rotation + degrees) % 360;
    setRotation(newRotation);
    updateImageAttribute({ rotation: newRotation }, true);
  };

  const handleWrapChange = (val) => {
    setWrap(val);
    updateImageAttribute({ wrap: val });
  };

  const handleAltChange = (e) => {
    const val = e.target.value;
    setAlt(val);
    updateImageAttribute({ alt: val });
  };

  const handleBorderColorChange = (e) => {
    const val = e.target.value;
    setBorderColor(val);
    updateImageAttribute({ borderColor: val });
  };

  const handleBorderWidthChange = (e) => {
    const val = parseInt(e.target.value) || 0;
    setBorderWidth(val);
    updateImageAttribute({ borderWidth: val });
  };

  const handleBorderStyleChange = (val) => {
    setBorderStyle(val);
    updateImageAttribute({ borderStyle: val });
  };

  const handleBorderRadiusChange = (e) => {
    const val = parseInt(e.target.value) || 0;
    setBorderRadius(val);
    updateImageAttribute({ borderRadius: val });
  };

  const handleOpacityChange = (e) => {
    const val = parseInt(e.target.value) || 100;
    setOpacity(val);
    updateImageAttribute({ opacity: val });
  };

  const handleCopyUrl = () => {
    if (selectedNode?.attrs?.src) {
      navigator.clipboard.writeText(selectedNode.attrs.src);
      setCopiedUrl(true);
      toast.success('URL copiada');
      setTimeout(() => setCopiedUrl(false), 2000);
    }
  };

  const handleDelete = () => {
    if (editor && selectedNodeIsImage) {
      editor.chain().focus().deleteSelection().run();
      onClose?.();
      toast.success('Imagen eliminada');
    }
  };

  const handleDownload = () => {
    const src = selectedNode?.attrs?.src;
    if (src) downloadAsset(src, src.split('/').pop());
  };

  const handleAddToGallery = async () => {
    const src = selectedNode?.attrs?.src;
    if (!src) return;
    try {
      await axios.post(
        `${API}/images/gallery`,
        {
          url: src,
          alt_text: alt || '',
          source: 'upload',
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success('Imagen cargada a la galería');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar a la galería');
    }
  };

  useEffect(
    () => () => {
      if (holdIntervalRef.current) clearInterval(holdIntervalRef.current);
      if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);
    },
    [],
  );

  const startHoldIncrement = useCallback((incrementFn) => {
    if (holdIntervalRef.current) clearInterval(holdIntervalRef.current);
    if (holdTimeoutRef.current) clearTimeout(holdTimeoutRef.current);

    incrementFn();

    holdTimeoutRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => {
        incrementFn();
      }, 100);
    }, 300);
  }, []);

  const stopHoldIncrement = useCallback(() => {
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
  }, []);

  const incrementWidth = useCallback(
    (amount) => {
      setWidth((prev) => {
        const currentVal = parseInt(prev) || naturalWidth || 100;
        const newVal = Math.min(currentVal + amount, MAX_CONTENT_WIDTH);

        updateImageAttribute({
          width: newVal,
          height: height ? parseInt(height) : selectedNode?.attrs?.height,
        });

        return newVal.toString();
      });
    },
    [naturalWidth, height, updateImageAttribute, selectedNode],
  );

  const decrementWidth = useCallback(
    (amount) => {
      setWidth((prev) => {
        const currentVal = parseInt(prev) || naturalWidth || 100;
        const newVal = Math.max(currentVal - amount, 50);

        updateImageAttribute({
          width: newVal,
          height: height ? parseInt(height) : selectedNode?.attrs?.height,
        });

        return newVal.toString();
      });
    },
    [naturalWidth, height, updateImageAttribute, selectedNode],
  );

  const incrementHeight = useCallback(
    (amount) => {
      setHeight((prev) => {
        const currentVal = parseInt(prev) || naturalHeight || 100;
        const newVal = Math.min(currentVal + amount, MAX_CONTENT_HEIGHT);

        updateImageAttribute({
          width: width ? parseInt(width) : selectedNode?.attrs?.width,
          height: newVal,
        });

        return newVal.toString();
      });
    },
    [naturalHeight, width, updateImageAttribute, selectedNode],
  );

  const decrementHeight = useCallback(
    (amount) => {
      setHeight((prev) => {
        const currentVal = parseInt(prev) || naturalHeight || 100;
        const newVal = Math.max(currentVal - amount, 50);

        updateImageAttribute({
          width: width ? parseInt(width) : selectedNode?.attrs?.width,
          height: newVal,
        });

        return newVal.toString();
      });
    },
    [naturalHeight, width, updateImageAttribute, selectedNode],
  );

  return {
    selectedNodeIsImage,
    width,
    height,
    rotation,
    wrap,
    alt,
    copiedUrl,
    naturalWidth,
    naturalHeight,
    aspectRatioLocked,
    borderColor,
    borderWidth,
    borderStyle,
    borderRadius,
    opacity,
    MAX_CONTENT_WIDTH,
    MAX_CONTENT_HEIGHT,
    setAspectRatioLocked,
    handleWidthChange,
    handleHeightChange,
    handleRotationChange,
    handleRotateButton,
    handleWrapChange,
    handleAltChange,
    handleBorderColorChange,
    handleBorderWidthChange,
    handleBorderStyleChange,
    handleBorderRadiusChange,
    handleOpacityChange,
    handleCopyUrl,
    handleDelete,
    handleDownload,
    handleAddToGallery,
    startHoldIncrement,
    stopHoldIncrement,
    incrementWidth,
    decrementWidth,
    incrementHeight,
    decrementHeight,
  };
};

export default useImageProperties;
